import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {OpenAIService} from "../../services/openai/openai.service";
import {UtilService} from "../../services/util/util.service";
import {ConversationSessionOptions} from "../../services/openai/conversation-session-options";
import {ConversationRequestContext} from "../../services/openai/conversation-request-context";
import {ConversationSessionType} from "../../services/openai/conversation-session-type";
import {
  ConversationSessionAnalyseRecordOptions
} from "../../services/openai/conversation-session-analyse-record-options";
import {
  ConversationSessionAnalyseConversationOptions
} from "../../services/openai/conversation-session-analyse-conversation-options";
import {ConversationSessionChatOptions} from "../../services/openai/conversation-session-chat-options";
import {
  ConversationResponseChatCompletionChunk
} from "../../services/openai/conversation-response-chat-completion-chunk";
import {interval, Subject, Subscription, zip} from "rxjs";
import {takeWhile} from "rxjs/operators";
import {ListViewComponent, SelectEventArgs} from "@syncfusion/ej2-angular-lists";
import {DialogComponent} from "@syncfusion/ej2-angular-popups";
import {ButtonPropsModel} from "@syncfusion/ej2-popups/src/dialog/dialog-model";
import {cloneDeep} from 'lodash';
import {UserService} from "../../services/user/user.service";
import {InternationalizationService} from "../../services/internationalization/internationalization.service";

@Component({
  selector: 'sb-openai-chat',
  templateUrl: './openai-chat.component.html',
  styleUrls: ['./openai-chat.component.scss'],
})
export class OpenAIChatComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input()
  sessionType: ConversationSessionType = ConversationSessionType.ANALYSE_RECORD;

  @Input()
  headerTitle = "Chat";

  @Input()
  showHeader = true;

  @Input()
  welcomeMessage: string = "Welcome to a chat with ChatGPT";

  @Input()
  systemPrompt: string = "You are an ITSM service agent. Your task is to answer questions about the data you are presented and also provide information such as the actors involved.";

  @Input()
  prompt: string;

  @Input()
  count: number = 1;

  @Input()
  width = "500px";

  /**
   * Note that this is the height of the content area not including the header.
   */
  @Input()
  height = "500px";

  @Input()
  recordId: string = null;

  @Input()
  recordTable: string = null;

  @Input()
  conversationId: string;

  @Input()
  set nextPrompt(value: string) {
    this.prompt = value;
    this.handlePrompt();
  }

  @Output()
  lastCompletion: EventEmitter<string> = new EventEmitter<string>();

  fields = { text: "text", id: "id" };

  @ViewChild('listView')
  listView: ListViewComponent;

  @ViewChild('configureDialog')
  configureDialog: DialogComponent;

  @ViewChild('input')
  input: any;

  options: ConversationSessionOptions & (ConversationSessionAnalyseRecordOptions | ConversationSessionAnalyseConversationOptions | ConversationSessionChatOptions);

  analyseRecordOptions: ConversationSessionAnalyseRecordOptions = {
    recordId: this.recordId,
    recordTable: this.recordTable,
    clientJournal: true,
    workJournal: true,
    majorIncidentJournal: true,
    auditHistory: true,
    recordFields: true,
  };

  analyseConversationOptions: ConversationSessionAnalyseConversationOptions;

  chatOptions: ConversationSessionChatOptions;

  context: ConversationRequestContext;

  choices: string[] = [];

  choiceQueue: Subject<any> = new Subject<any>();

  choiceQueue$: Subscription;

  choiceCompleted = false;

  lastListIndex = 0;

  public initialData: { [key: string]: Object; }[] = [];

  public dataSource: { [key: string]: Object; }[] = [];

  // Configure dialog

  public dialogPosition: any = { X: 'center', Y: 'center' };

  public dialogTitle = "Configure";

  public dialogButtons: ButtonPropsModel[] = [
    {
      'click': this.hideDialog.bind(this, false),
      buttonModel: {
        content: 'Cancel',
      }
    },
    {
      'click': this.hideDialog.bind(this, true),
      buttonModel: {
        content: 'Save',
        isPrimary: true,
      }
    },
  ];

  initialAnalyseRecordOptions: ConversationSessionAnalyseRecordOptions = cloneDeep(this.analyseRecordOptions);

  initialSystemPrompt: string = cloneDeep(this.systemPrompt);

  initialRecordId: string = cloneDeep(this.recordId);

  initialRecordTable: string = cloneDeep(this.recordTable);

  // Initial setup
  contentHeightSet = false;

  constructor(private openaiService: OpenAIService,
              private utilService: UtilService,
              private userService: UserService,
              private i18nService: InternationalizationService) { }

  async ngOnInit() {

    this.initialData = [
      {
        text: "System",
        message: this.welcomeMessage,
        id: "-1",
        role: "initial",
        avatar: "S"
      },
    ];
    this.dataSource = cloneDeep(this.initialData);

    this.resetBeforeRequest(); // TODO do we need to do this here?

    this.initOptions();

    await this.sendCreate();
  }

  async ngOnDestroy() {
    this.choiceQueue$ && this.choiceQueue$.unsubscribe()
  }

  ngAfterViewInit() {
  }

  getLastCompletion(): string {
    const item = this.listView.findItem({ id: this.lastListIndex});
    return (item as any)?.message;
  }

  private initOptions() {
    this.options = {
      clientSessionId: this.utilService.generateUUID(),
      sessionType: this.sessionType,
      count: this.count,
      callback: this.responseCallback.bind(this)
    };

    this.context = null;
  }

  private getOptions(regenerate: boolean = false) {

    let otherOptions = {};
    if (this.sessionType == ConversationSessionType.ANALYSE_RECORD) {
      this.analyseRecordOptions.recordTable = this.recordTable;
      this.analyseRecordOptions.recordId = this.recordId;
      otherOptions = this.analyseRecordOptions;
    } else if (this.sessionType == ConversationSessionType.ANALYSE_CONVERSATION) {
      otherOptions = this.analyseConversationOptions;
    } else if (this.sessionType == ConversationSessionType.CHAT) {
      otherOptions = this.chatOptions;
    }

    return {...this.options, ...(otherOptions || {}), ...{regenerate: regenerate}};
  }

  private getContext(): ConversationRequestContext {
    return {
      prompt: this.prompt,
      systemPrompt: this.systemPrompt
    };
  }

  private resetBeforeRequest() {
    this.choices = Array(this.count).fill("");

    this.choiceQueue$ && this.choiceQueue$.unsubscribe();

    this.choiceCompleted = false;

    // This RxJS zip/interval thing is to get the response to update the screen periodically
    this.choiceQueue$ = zip(interval(100), this.choiceQueue).pipe(takeWhile(() => !this.choiceCompleted)).subscribe((next) => {
      const choices = next[1] as string[];

      // This is the part that updates the item that the bot is writing
      const item = this.listView.findItem({ id: this.lastListIndex});
      (item as any).message = choices[0];

      this.scrollToAnchor();
    });

  }

  private addScrollAnchor() {
    this.listView.removeItem({id: "anchor"});
    this.addListItem([{ text: 'Anchor', id: 'anchor', role: "anchor" }]);
  }

  private scrollToAnchor() {
    // This doesn't retrieve the item, just the data
    // const anchor = this.listView.findItem({ id: 'anchor'});

    // This is an undocumented method that works
    const anchor = (this.listView as any).getLiFromObjOrElement({id: 'anchor'});
    if (anchor) {
      anchor.scrollIntoView({behavior: "smooth", block: "end", inline: "nearest"});
    }
  }

  private
  private responseCallback(data: ConversationResponseChatCompletionChunk, combinedChunks: {[key: number]:string}, type: string) {
    if (type == 'CHUNK') {
      data.choices.forEach((choice) => {
        const index = choice.index;
        const delta = choice.delta;
        const finishReason = choice.finish_reason;
        if (!this.choices[index]) {
          this.choices[index] = "";
        }
        this.choices[index] = this.choices[choice.index] + (delta.content || "");
      });

      this.choiceQueue.next(this.choices);
    } else if (type == "COMPLETED") {
      this.choiceCompleted = true;

      this.lastCompletion.emit(this.choices[0]);
    }
  }

  private async sendRequest(regenerate: boolean = false) {
    this.resetBeforeRequest();
    const serverResponse = await this.openaiService.sendChatSession(this.getOptions(regenerate), this.getContext());
  }

  private async sendCreate() {
    const serverResponse = await this.openaiService.createChatSession(this.getOptions(), this.context);
  }

  public click() {
    let value = this.input.nativeElement.value;

    this.input.nativeElement.value = "";

    this.prompt = value;

    this.handlePrompt();
  }

  onKeyDown(e: KeyboardEvent) {
    if (e.key == 'Enter') {
      this.click();
    }
  }

  private handlePrompt() {
    this.lastListIndex++;

    this.addListItem([{ text: this.userService.getFirstName() + " " + this.userService.getLastName(), message: this.prompt, id: String(this.lastListIndex), avatar: this.userService.getInitials(), pic: "", role: "user" }]);

    this.lastListIndex++;

    this.addListItem([{ text: 'ChatGPT', message: "", id: String(this.lastListIndex), avatar: "C", pic: "", role: "assistant" }]);

    this.addScrollAnchor();

    this.utilService.tickThen(() => {
      this.scrollToAnchor();
    });

    this.sendRequest();
  }

  private user() {
    return this.userService;
  }

  private addListItem(data: any[]) {

    const formatted = this.i18nService.formatLongDateTime(new Date());

    data.forEach((d) => {
      d.date = formatted;
    });

    this.listView.addItem(data);
  }

  // Buttons

  configure(e: MouseEvent) {
    this.initialAnalyseRecordOptions = cloneDeep(this.analyseRecordOptions);
    this.initialSystemPrompt = cloneDeep(this.systemPrompt);
    this.initialRecordTable = cloneDeep(this.recordTable);
    this.initialRecordId = cloneDeep(this.recordId);
    this.configureDialog.show();
  }

  async regenerate(e: MouseEvent) {
    this.prompt = "";
    await this.sendRequest(true);
  }

  copyTranscript(e: MouseEvent) {
    // TODO: copy the entire transcript to the clipboard
  }

  clearTranscript(e: MouseEvent) {
    this.initOptions();
    this.dataSource = [];
    this.utilService.tickThen(async () => {
      this.dataSource = cloneDeep(this.initialData);
      await this.sendCreate();
    });
  }

  // Configure

  // Close means the 'x' was clicked or ESC was pressed
  closeDialog(e: any) {
    if (e.closedBy == 'escape' || e.closedBy == 'close icon') {
      this.analyseRecordOptions = cloneDeep(this.initialAnalyseRecordOptions);
      this.systemPrompt = cloneDeep(this.initialSystemPrompt);
      this.recordTable = cloneDeep(this.initialRecordTable);
      this.recordId = cloneDeep(this.initialRecordId);
    }
  }

  // Hide means a button was pressed
  public hideDialog(save: boolean) {

    if (save == false) {
      this.analyseRecordOptions = cloneDeep(this.initialAnalyseRecordOptions);
      this.systemPrompt = cloneDeep(this.initialSystemPrompt);
      this.recordTable = cloneDeep(this.initialRecordTable);
      this.recordId = cloneDeep(this.initialRecordId);
    }

    this.configureDialog.hide();
  }

  onSelect(e: SelectEventArgs) {
    // We don't want selections on the list to be visible
    e.cancel = true;
    // Bug in syncfusion requires us to do this.
    e.item.classList.remove('e-active');

    // TODO: copy the selected text to the clipboard
  }

  onActionComplete(e: any) {
    // The first time this is called the content container is available so we can set the height
    if (!this.contentHeightSet) {
      (this.listView as any).contentContainer.style.height = this.height;
      this.contentHeightSet = true;
    }
  }

  // Hack to shut IJ up
  inputWidth() {
    return `width: ${this.width};`
  }
}
